/**
 * Date civile d'un formulaire de gabarit (R1, ADR-085) : AAAA-MM-JJ, sans fuseau, jamais un instant.
 * La date du jour qui borne le calendrier se prend au fuseau du site, sinon du tenant, jamais en UTC
 * par défaut : sans fuseau connu, aucune borne (le serveur reste juge de la réponse).
 */

const CIVIL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Le calendrier propose deux ans en arrière et un an en avant (décision de l'utilisateur, 2026-10-18). */
export const DATE_YEARS_BEFORE = 2;
export const DATE_YEARS_AFTER = 1;

export function isCivilDate(value: unknown): value is string {
  return typeof value === 'string' && CIVIL_DATE.test(value);
}

/** Fuseaux candidats de la date du jour : le site d'abord, puis le tenant. */
export interface CivilTimeZones {
  site?: string | undefined;
  tenant?: string | undefined;
}

/** Date civile d'un instant dans un fuseau IANA ; `null` pour un fuseau inconnu. */
export function civilDateIn(instant: Date, timeZone: string): string | null {
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(instant).map((part) => [part.type, part.value]));
    const civil = `${parts['year']}-${parts['month']}-${parts['day']}`;
    return isCivilDate(civil) ? civil : null;
  } catch {
    return null;
  }
}

/** Date du jour au fuseau du site, sinon du tenant ; `null` sans fuseau valide (jamais UTC par défaut). */
export function civilToday(now: Date, zones: CivilTimeZones): string | null {
  for (const zone of [zones.site, zones.tenant]) {
    if (typeof zone !== 'string' || zone === '') continue;
    const civil = civilDateIn(now, zone);
    if (civil !== null) return civil;
  }
  return null;
}

/** Même jour, `years` ans plus tôt ou plus tard ; un 29 février sans année bissextile devient le 28. */
export function addCivilYears(date: string, years: number): string {
  const match = CIVIL_DATE.exec(date);
  if (match === null) throw new RangeError(`Date civile attendue : ${date}`);
  const year = Number(match[1]) + years;
  const month = match[2]!;
  let day = match[3]!;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  if (month === '02' && day === '29' && !leap) day = '28';
  return `${String(year).padStart(4, '0')}-${month}-${day}`;
}

/** Bornes du calendrier autour de la date du jour ; aucune sans date du jour. */
export function civilDateBounds(today: string | null): { min: string; max: string } | null {
  if (today === null || !isCivilDate(today)) return null;
  return { min: addCivilYears(today, -DATE_YEARS_BEFORE), max: addCivilYears(today, DATE_YEARS_AFTER) };
}
